import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { ErrorMessage } from '@hookform/error-message';
import CustomerPortfolioService from '../../../../SLA/CustomerPortfolio/service';
import { Row, Form, Col, Input } from 'reactstrap';

import { toast } from 'react-toastify';
import ReactButton from 'components/Buttons/ReactButton';
import Loader from 'components/Loader';
import CustomFieldsInputsForm from '../../../shared/components/CustomFieldsInputsForm';
import AccountService from '../../services/accountService';

import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { formatCustomFieldsToDisplay } from 'views/CRM/shared/helpers/customFieldsHelpers';
import { removeMaskSpecialCharacters } from 'views/CRM/shared/utils/phoneNumberHelpers';
import { formatCustomFieldsToSubmit } from 'views/CRM/shared/helpers/customFieldsHelpers';
import TemplatesModal from 'views/ServicePanel/components/QueueContent/components/Contact/TemplatesModal';
import { ScreenPrompt } from 'components/ScreenPrompt';
import FlexSelect from 'components/Inputs/FlexSelect';
import { FlexController } from 'components/FlexControllerInput';
import { getDefaultCountry } from 'services/ServiceCOUNTRYS';
import { normalizePhoneNumber } from 'utils/fuctions/internationalPhoneFormatter';
import { normalizeDDI } from 'utils/fuctions/internationalPhoneFormatter';
import { useUsers } from 'views/Report/Protocol/hooks/useUsers';
import { verifyRoles } from 'components/Authentication/VerifyRoles';

/**
 * vou considerar que é a página de edição caso:
 * 1 - a href da página termine em /:recordTypeId/:id
 * 2 - para isso, vou testar se termina em dois números
 */
function isEditPage() {
  const split = window.location.href.split('/');

  const [last, penultimate] = [split?.at?.(-1), split?.at?.(-2)];

  return !Number.isNaN(Number(last)) && !Number.isNaN(Number(penultimate));
}

const AccountInputs = ({
  recordTypeId,
  account,
  showCallIconOnPhoneField = false,
  showMessageIconOnPhoneField = false,
  setSelectedClient = () => {},
  closeModal = () => {},
  editAccount = false
}) => {
  const history = useHistory();
  const { userList } = useUsers();
  const login = localStorage.getItem('login');
  const { control, handleSubmit, errors, setValue, reset, formState, watch } =
    useForm();

  const { isDirty } = formState;

  const [isSubmiting, setIsSubmiting] = useState(false);
  const [submitAttempt, setSubmitAttempt] = useState(false);
  const [customerPortfolios, setCustomerPortfolios] = useState([]);
  const [isUpdate, setIsUpdate] = useState(false);
  const [
    isModalTemplateMessageAccountOpen,
    setIsisModalTemplateMessageAccountOpen
  ] = useState(false);
  const [
    whatsAppMessageContactInfoAccount,
    setWhatsAppMessageContactInfoAccount
  ] = useState({});

  const { getCustomFields, customFields, isLoadingCustomFields } =
    useEntityCustomFieldsByRecordType(entitiesName.ACCOUNT, recordTypeId);

  useEffect(() => {
    if (account) {
      setIsUpdate(true);
      const formattedAccount = formatCustomFieldsToDisplay(
        {
          ...account,
          phone: account?.phone
        },
        customFields
      );

      const ddi = formattedAccount?.ddi?.startsWith?.('+')
        ? formattedAccount?.ddi?.replace?.('+', '')
        : formattedAccount?.ddi;

      const resetValues = {
        ...formattedAccount,
        phone: {
          ddi,
          number: formattedAccount?.phone
        }
      };

      reset(resetValues);
    }
  }, [reset, account, customFields]);

  useEffect(() => {
    CustomerPortfolioService.getWithoutPagination()
      .then((response) => {
        const data = response.data.data.content.map((item) => ({
          name: item.name,
          id: item.id
        }));
        setCustomerPortfolios(data);
      })
      .catch((err) => {
        toast.error(
          err.response?.data?.message ||
            err.message ||
            'Erro ao buscar categorias dos clientes'
        );
      });
  }, []);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  function handleWhatsAppMessage(phone) {
    let ddi = '';
    let number = '';

    if (typeof phone === 'object' && phone !== null) {
      ddi = phone.ddi || '';
      number = phone.number || '';
    } else if (typeof phone === 'string') {
      number = phone;
    }

    const phoneNumberWithoutMask = removeMaskSpecialCharacters(number);
    let data = {
      nome: account?.name,
      telefone: phoneNumberWithoutMask !== '' ? phoneNumberWithoutMask : '',
      ddi: ddi
    };

    setWhatsAppMessageContactInfoAccount(data);
    setIsisModalTemplateMessageAccountOpen(!isModalTemplateMessageAccountOpen);
    return whatsAppMessageContactInfoAccount;
  }

  const onSubmit = async (data) => {
    setIsSubmiting(true);
    setSubmitAttempt(true);

    const dataDTO = formatCustomFieldsToSubmit(data, customFields);

    dataDTO.phone = normalizePhoneNumber(data?.phone?.number);
    dataDTO.ddi = normalizeDDI(
      data?.phone?.ddi || getDefaultCountry()?.dialCode
    );

    try {
      const saveAccountPromise = account
        ? await AccountService.update(account.id, dataDTO)
        : await AccountService.create(recordTypeId, dataDTO);

      toast.success(`Conta ${account ? 'atualizada' : 'criada'} com sucesso`);

      if (editAccount) {
        setSelectedClient({
          clientId: account?.id,
          clientName: dataDTO?.name,
          accountRecordTypeId: recordTypeId
        });
        return closeModal();
      }

      // reset to clear the form and isDirty property
      // não resetar caso seja o modal de edição da conta
      // eslint-disable-next-line no-unused-expressions
      if (!isEditPage()) reset();

      if (verifyRoles(['CRM_ACCOUNTS_EDIT'])) {
        history.push(
          `/admin/crm/accounts/${recordTypeId}/${
            saveAccountPromise?.data?.data?.id || account.id
          }`
        );
      } else if (!verifyRoles(['CRM_ACCOUNTS_EDIT'])) {
        history.push(`/admin/crm/accounts`);
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          `Erro ao ${account ? 'atualizar' : 'criar'} conta`
      );
    } finally {
      setIsSubmiting(false);
    }
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/crm/accounts', { recordTypeId });
  };

  if (isLoadingCustomFields) {
    return (
      <Row className="justify-content-md-center">
        <Loader />
      </Row>
    );
  }
  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form className="needs-validation" onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col sm="6" className="mb-3">
            {/* TO_DO: alterar esse input para o FlexController, para manter a consistência */}
            <label className="form-control-label text-capitalize">
              Nome <span style={{ color: 'red' }}>*</span>
            </label>
            <Controller
              render={(props) => (
                <Input
                  value={props.value}
                  onChange={props?.onChange}
                  type="text"
                  placeholder="Nome"
                />
              )}
              control={control}
              defaultValue=""
              rules={{ required: 'Campo obrigatório' }}
              name="name"
            />
            <ErrorMessage
              errors={errors}
              name="name"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>

          <Col className="mb-3">
            <FlexController
              type="phone"
              label="Telefone"
              name="phone"
              disabled={false}
              showMessageButton={showMessageIconOnPhoneField}
              showCallButton={showCallIconOnPhoneField}
              handleSendTemplate={handleWhatsAppMessage}
              watch={watch}
              value={watch('phone')}
              control={control}
              errors={errors}
              required={false}
              rules={{ required: false }}
              validateOnSubmit={submitAttempt}
              valueController={(fieldName, value) => {
                setValue(fieldName, value);
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col sm="6" className="mb-3">
            {/* TO_DO: alterar esse input para o FlexController, para manter a consistência */}
            <label className="form-control-label text-capitalize">
              Categoria dos Clientes <span style={{ color: 'red' }}>*</span>
            </label>
            <Controller
              control={control}
              name="customerPortfolioId"
              render={(props) => (
                <FlexSelect
                  rules={
                    props?.field?.rules || { required: 'Campo obrigatório' }
                  }
                  isClearable={true}
                  dataOptions={customerPortfolios || []}
                  isMulti={false}
                  closeMenuOnSelect={true}
                  value={props.value}
                  valueController={setValue}
                  fieldName="customerPortfolioId"
                  labelName="name"
                  valueName="id"
                  defaultValue={account?.customerPortfolioId || ''}
                />
              )}
              defaultValue={customerPortfolios[0]?.id || ''}
              rules={{ required: 'Campo obrigatório' }}
            />
            <ErrorMessage
              errors={errors}
              name="customerPortfolioId"
              render={({ message }) => errorFormMessage(message)}
            />
          </Col>
          <Col sm="6">
            <label className="form-control-label">Responsável</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={userList || []}
                  isMulti={false}
                  value={props.value}
                  valueController={setValue}
                  fieldName="responsible"
                  labelName="name"
                  valueName="login"
                  isClearable={true}
                  closeMenuOnSelect={true}
                />
              )}
              control={control}
              name="responsible"
              defaultValue={login}
            />
          </Col>
        </Row>
        <Row className="mb-1">
          <CustomFieldsInputsForm
            fields={customFields}
            control={control}
            errors={errors}
            setValue={setValue}
            Controller={Controller}
            options={{ showCallIconOnPhoneField, showMessageIconOnPhoneField }}
            contactMessageClientName={account?.name}
            watch={watch}
            isUpdate={isUpdate}
            isOnEditPage={isUpdate}
          />
        </Row>

        <ReactButton
          btnColor="confirmation"
          type="submit"
          disabled={isSubmiting}
        >
          Salvar
        </ReactButton>

        {!account && (
          <ReactButton
            btnColor="cancelation"
            onClick={() => {
              return cancelAction();
            }}
          >
            Cancelar
          </ReactButton>
        )}
      </Form>
      <TemplatesModal
        isModalOpen={isModalTemplateMessageAccountOpen}
        onModalToggle={setIsisModalTemplateMessageAccountOpen}
        clientNumber={whatsAppMessageContactInfoAccount.telefone}
        clientDDI={whatsAppMessageContactInfoAccount.ddi}
        clientName={whatsAppMessageContactInfoAccount.nome}
        clientId={whatsAppMessageContactInfoAccount.clientId ?? ''}
        isIsolated={true}
      />
    </>
  );
};

export default AccountInputs;
